// The six demo students as Mosaico "managed students", for the Students
// page (Teacher → Students). Read-only cards: no edit / reset-password /
// activity actions; "View disc" opens the ArcDisk student page under the
// project the student belongs to.
import type { ManagedStudent } from 'api/arcMosaico/types'
import { demoStudentList, projectFixtures } from './fixtures'
import { DEMO_PROJECTS, demoStudentPath } from './projects'

const ID_PREFIX = 'arcdisk-student-'

export const demoStudentCardId = (studentId: number) => `${ID_PREFIX}${studentId}`

export const isDemoStudentCardId = (id: string | undefined): boolean => !!id && id.startsWith(ID_PREFIX)

function adProjectIdForStudent(studentId: number): string | undefined {
  return Object.values(projectFixtures).find(f => f.dashboard.students.some(s => s.student_id === studentId))?.dashboard
    .meta.project_id
}

/** Route of the demo student page for a demo card id, or undefined for a real student. */
export function demoStudentDiscPath(cardId: string | undefined): string | undefined {
  if (!isDemoStudentCardId(cardId)) return undefined
  const studentId = Number((cardId as string).slice(ID_PREFIX.length))
  const adProjectId = adProjectIdForStudent(studentId)
  const ref = DEMO_PROJECTS.find(p => p.adProjectId === adProjectId)
  // `from=students` makes the student page's primary back link return here
  return ref ? `${demoStudentPath(ref.cardId, studentId)}?from=students` : undefined
}

export function demoManagedStudents(): ManagedStudent[] {
  return demoStudentList.map(s => {
    const [firstName, ...rest] = s.student_name.split(/\s+/)
    const adProjectId = adProjectIdForStudent(s.student_id)
    const grade = adProjectId ? projectFixtures[adProjectId].dashboard.meta.grade : null
    return {
      id: demoStudentCardId(s.student_id),
      firstName,
      lastName: rest.join(' '),
      username: `arcdisk.${s.student_id}`,
      email: null,
      status: 'active',
      inviteStatus: 'registered',
      avatarUrl: null,
      groups: [],
      grades: grade ? [{ id: `arcdisk-grade-${grade}`, name: `Grade ${grade}` }] : [],
      permissions: {
        editProfile: false,
        resetPassword: false,
        viewProfile: false,
        viewDisc: true,
        editLegalGuardians: false,
        remove: false,
      },
      createdAt: new Date(0).toISOString(),
    }
  })
}

export interface StudentListContext {
  page: number
  search: string
  groupId: string | null
}

/** Append the demo students to page 1 of an unfiltered Students list. */
export function withDemoStudents(students: ManagedStudent[], ctx: StudentListContext): ManagedStudent[] {
  if (ctx.page !== 1 || ctx.search.trim() !== '' || ctx.groupId) return students
  return [...students, ...demoManagedStudents()]
}
